import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';

import { showModal } from '../actions/modal';

import { typingStart, typingEnd } from 'api/global-api';

import { debugLog as log } from 'shared-services/logger-service';

import config from '../config';

import { trackEvent } from '../services/events-tracker-service';
import { canUser } from '../services/permissions-service';
import { getAssociationByType } from '../utils/message';

import { SocketContext } from '../context/SocketContext';

import CanUser from './can-user';
import InputTextEditor from './input-text-editor';
import Toggle from './toggle';
import FileUpload from './file-upload';
import DropPanel from './drop-panel';
import GifPicker from './gif-picker';
import EmojiPicker from './emoji-picker';
import UsersTyping from './users-typing';
import GoogleFileChooser from './google-file-chooser';
import DropboxFileChooser from './dropbox-file-chooser';
import DiscussionUtilGoogleDoc from './discussion-util-google-doc';
import Tooltip from './tooltip';

import IconGif from '../icons/icon-gif';
import IconEmoji from '../icons/icon-emoji';
import IconFile from '../icons/icon-file';
import IconDoc from '../icons/icon-doc';
import IconSend from '../icons/icon-send';
import IconUpload from '../icons/icon-upload';
import IconPlus from '../icons/icon-plus';
import IconDropbox from '../icons/icon-dropbox';
import IconGDrive from '../icons/icon-gdrive';
import IconEye from '../icons/icon-eye';
import IconEyeStrike from '../icons/icon-eye-strike';

class DiscussionInput extends PureComponent {
    static contextType = SocketContext;

    static defaultProps = {
        utils: ['gif', 'emoji', 'file', 'doc']
    }

    constructor(props) {
        super(props);

        this.editorCtrl = null;
        this.typingEndTimeout = null;

        this.docCache = {
            name: null,
            content: null
        };

        this.elementHeight = 0;

        this.state = {
            focus: null,
            dragOver: false,
            additionalVisible: false,
            enterToSubmit: true,
            hideFromGuests: false
        };
    }

    handleFocus = () => {
        this.setState({
            focus: true
        });

        this.props.onFocus && this.props.onFocus();
    }

    handleBlur = () => {
        this.setState({
            focus: false
        });

        this.stopTyping();

        this.props.onBlur && this.props.onBlur();
    }

    handleDocCreateClick = () => {
        showModal('DOCUMENT_MODAL', {
            projectId: this.props.projectId,
            docName: this.docCache.name,
            docContent: this.docCache.content,
            members: this.props.users.map(user => user.id),
            onCreate: (docId, docName) => {
                this.submit({
                    type: 'document',
                    doc_attachment: docId
                });

                trackEvent('DOC.CREATE', {
                    via: 'Discussion',
                    id: docId,
                    docName
                });

                // Clear after close
                setTimeout(() => {
                    this.clearDocCache();
                });
            },
            onClose: modalCtrl => {
                if (modalCtrl) {
                    this.docCache.name = modalCtrl.getDocName();
                    this.docCache.content = modalCtrl.getDocContent();
                }
            }
        });

        this.setState({
            additionalVisible: false
        });
    }

    handleAdditionalToggleClick = () => {
        this.setState({
            additionalVisible: this.state.additionalVisible ? false : 'all'
        });
    }

    handleAdditionalUploadClick = () => {
        this.setState({
            additionalVisible: 'upload'
        });
    }

    handleAdditionalCreateClick = () => {
        this.setState({
            additionalVisible: 'create'
        });
    }

    clearDocCache() {
        this.docCache.name = null;
        this.docCache.content = null;
    }

    handleSubmit = text => {
        this.submit({
            text: text.replace(/\n$/, '')
        });
    }

    handleKeyDown = () => {
        this.startTyping(this.editorCtrl && this.editorCtrl.isPopulated());
    }

    handleGifSelect = url => {
        this.submit({
            url,
            type: 'gif'
        });
    }

    handleUploadConfirm = (files, text) => {
        this.submit({ text }, files);
    }

    handleFilesAdd = files => {
        if (this.props.directUpload) {
            this.submit({ text: this.editorCtrl.getValue() }, files);
            this.editorCtrl && this.editorCtrl.reset();
        } else {
            showModal('FILE_UPLOAD', {
                files,
                mentionUsers: this.props.users,
                onUpload: this.handleUploadConfirm
            });
        }

        this.setState({
            additionalVisible: false
        });
    }

    handleExternalFilesAdd = files => {
        this.props.onNewMessage && this.props.onNewMessage('', files);

        this.setState({
            additionalVisible: false
        });
    }

    handleGoogleDocsFileClick = (type, token) => {
        showModal('GOOGLE_DOC_CREATE', {
            projectId: this.props.projectId,
            type,
            token,
            navigateOnSuccess: false,
            onCreate: (docId, docName) => {
                showModal('DOCUMENT_MODAL', {
                    type: 'google',
                    docId
                });

                this.submit({
                    type: 'document',
                    doc_attachment: docId
                });
            }
        });

        this.setState({
            additionalVisible: false
        });
    }

    startTyping(populated) {
        if (populated) {
            if (!this.typing) {
                this.typing = true;

                if (this.context && this.context.emitEvent) {
                    this.context.emitEvent('message_typing_start', {
                        association: this.associationName,
                        association_id: this.props.discussionId,
                        user_id: this.props.currentUserId
                    });
                } else {
                    typingStart({
                        association: this.associationName,
                        association_id: this.props.discussionId
                    });
                }

            }
        } else {
            this.stopTyping();
            return;
        }

        if (this.typingEndTimeout) {
            clearTimeout(this.typingEndTimeout);
        }

        const end = (associationName, discussionId) => {
            this.typingEndTimeout = setTimeout(() => this.stopTyping(associationName, discussionId), 3000);
        };

        end(this.associationName, this.props.discussionId);
    }


    stopTyping(associationName = this.associationName, discussionId = this.props.discussionId) {
        if (!this.typing) {
            return;
        }

        this.typing = false;

        if (this.typingEndTimeout) {
            clearTimeout(this.typingEndTimeout);
        }

        this.typingEndTimeout = null;

        if (this.context && this.context.emitEvent) {
            this.context.emitEvent('message_typing_end', {
                association: associationName,
                association_id: discussionId,
                user_id: this.props.currentUserId
            });
        } else {
            typingEnd({
                association: associationName,
                association_id: discussionId,
            });
        }

    }

    submit(message, files) {
        this.props.onNewMessage && this.props.onNewMessage(message, files, {
            hideFromGuests: this.state.hideFromGuests
        });
    }

    handleEnterToggleChange = enterToSubmit => {
        this.setState({ enterToSubmit });
    }

    handleChange = () => {
        this.detectHeightChange();

        this.forceUpdate();
    }

    handleSendClick = () => {
        if (this.editorCtrl.isPopulated()) {
            this.submit({
                text: this.editorCtrl.getValue()
            });

            this.editorCtrl.reset();
        }
    }

    handleEmojiSelect = ({ colons }) => {
        this.editorCtrl.insertEmoji(colons);
    }

    habdleToggleHideFromGuests = () => {
        this.setState({
            hideFromGuests: !this.state.hideFromGuests
        });
    }

    detectHeightChange() {
        if (!this.elementRef) {
            return;
        }

        const newHeight = this.elementRef.clientHeight;
        if (this.elementHeight === newHeight) {
            return;
        }

        this.elementHeight = newHeight;

        this.props.onHeightChange && this.props.onHeightChange();
    }

    focus() {
        this.editorCtrl && this.editorCtrl.focus();
    }

    getPaddingRight() {
        let paddingRight = 5 + 52;

        if (this.showHideFromGuests) {
            paddingRight += 32;
        }

        if (this.props.utils.includes('gif')) {
            paddingRight += 32;
        }

        if (this.props.utils.includes('emoji')) {
            paddingRight += 32;
        }

        if (!this.props.compact) {
            paddingRight *= 1.2;
        }

        return paddingRight;
    }

    setCtrl = ctrl => this.editorCtrl = ctrl;
    setElementRef = ref => this.elementRef = ref;

    get showHideFromGuests() {
        return this.props.canHideFromGuests && this.props.users.some(u => u?.role === 'client');
    }

    isPopulated() {
        return (this.editorCtrl && this.editorCtrl.isPopulated());
    }

    get associationName() {
        return getAssociationByType(this.props.type);
    }

    componentDidMount() {
        if (this.props.provideController) {
            this.props.provideController.current = {
                focus: () => {
                    log('[DISCUSSION INPUT] Focus from controller');
                    this.focus();
                }
            };
        }
    }

    componentWillUnmount() {
        this.stopTyping();

        if (this.props.provideController) {
            this.props.provideController.current = null;
        }
    }

    render() {
        const populated = this.isPopulated();
        const className = ['editor'];

        if (this.props.compact) {
            className.push('compact');
        }

        if (populated) {
            className.push('populated');
        }

        if (this.state.focus) {
            className.push('focus');
        }

        if (populated || this.state.focus) {
            className.push('expanded');
        }

        if (this.state.hideFromGuests) {
            className.push('hidden-from-guests');
        }

        const { t } = this.props;

        return (
            <div className={className.join(' ')} ref={this.setElementRef}>
                {
                    this.props.utils.includes('file') ? <DropPanel onChange={this.handleFilesAdd} className={this.props.localDropzone ? 'drop-panel-default drop-panel-local drop-panel-small' : 'drop-panel-default'} /> : null
                }

                <div className="editor-inner">
                    <UsersTyping discussionId={this.props.discussionId} />

                    <div className="editor-floating-controls">
                        {
                            this.props.showSendToggle ? (
                                <div className="editor-floating-control">
                                    <div className="editor-enter-toggle">
                                        <Toggle checked={true} onChange={this.handleEnterToggleChange} />

                                        { t('Enter to send') }
                                    </div>
                                </div>
                            ) : null
                        }
                    </div>

                    <div className="editor-floating-content">
                    </div>

                    <div className={'editor-additional' + (this.state.additionalVisible === 'all' ? ' visible' : '')}>
                        {
                            this.props.utils.includes('file') ? (
                                <div className="editor-additional-item-row" key="select-upload">
                                    <div className="editor-additional-item" onClick={this.handleAdditionalUploadClick}>
                                        <div className="editor-additional-item-inner">
                                            <div className="item-icon">
                                                <IconUpload />
                                            </div>

                                            <div className="item-body">
                                                { t('file:Upload a file from') }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        }

                        {
                            this.props.utils.includes('doc') && canUser('createDoc') ? (
                                <div className="editor-additional-item-row" key="select-create">
                                    <div className="editor-additional-item" onClick={this.handleAdditionalCreateClick}>
                                        <div className="editor-additional-item-inner">
                                            <div className="item-icon">
                                                <IconPlus />
                                            </div>

                                            <div className="item-body">
                                                { t('common:Create a new') }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        }
                    </div>

                    {
                        this.props.utils.includes('file') ? (
                            <div className={'editor-additional' + (this.state.additionalVisible === 'upload' ? ' visible' : '')}>
                                <div key="computer" className="editor-additional-item-row">
                                    <div key="file" className="editor-additional-item">
                                        <div className="editor-additional-item-inner">
                                            <FileUpload onChange={this.handleFilesAdd}>
                                                <div className="item-icon">
                                                    <IconFile />
                                                </div>

                                                <div className="item-body">
                                                    { t('common:Your Computer') }
                                                </div>
                                            </FileUpload>
                                        </div>
                                    </div>
                                </div>

                                <div key="gdrive" className="editor-additional-item-row">
                                    <div className="editor-additional-item" key="file">
                                        <div className="editor-additional-item-inner">
                                            <GoogleFileChooser onChange={this.handleExternalFilesAdd}>
                                                <div className="item-icon">
                                                    <IconGDrive />
                                                </div>

                                                <div className="item-body">
                                                    { t('apps:Google Drive') }
                                                </div>
                                            </GoogleFileChooser>
                                        </div>
                                    </div>
                                </div>

                                {
                                    config.DROPBOX.ENABLED ? (
                                        <div key="dropbox" className="editor-additional-item-row">
                                            <div className="editor-additional-item">
                                                <div className="editor-additional-item-inner">
                                                    <DropboxFileChooser onChange={this.handleExternalFilesAdd}>
                                                        <div className="item-icon">
                                                            <IconDropbox />
                                                        </div>

                                                        <div className="item-body">
                                                            { t('apps:Dropbox') }
                                                        </div>
                                                    </DropboxFileChooser>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null
                                }
                            </div>
                        ) : null
                    }

                    {
                        this.props.utils.includes('doc') ? (
                            <CanUser type="createDoc">
                                <div className={'editor-additional' + (this.state.additionalVisible === 'create' ? ' visible' : '')}>
                                    <div key="google-doc" className="editor-additional-item-row">
                                        <div key="doc" className="editor-additional-item">
                                            <DiscussionUtilGoogleDoc onItemClick={this.handleGoogleDocsFileClick} />
                                        </div>
                                    </div>

                                    <div key="doc" className="editor-additional-item-row">
                                        <div key="doc" className="editor-additional-item" onClick={this.handleDocCreateClick}>
                                            <div className="editor-additional-item-inner">
                                                <div className="item-icon">
                                                    <IconDoc />
                                                </div>

                                                <div className="item-body">
                                                    { t('doc:Nifty Document') }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CanUser>
                        ) : null
                    }

                    <div className="editor-main">
                        {
                            this.props.utils.includes('file') || (this.props.utils.includes('doc') && canUser('createDoc')) ? (
                                <div className={'editor-additional-toggle' + (this.state.additionalVisible ? ' active' : '')} onClick={this.handleAdditionalToggleClick}>
                                    <IconPlus />
                                </div>
                            ) : null
                        }

                        <InputTextEditor
                            id={this.props.discussionId}
                            placeholder={this.props.placeholder}
                            provideController={this.setCtrl}
                            users={this.props.users}
                            emptyOnSubmit={true}
                            enterToSubmit={this.state.enterToSubmit}
                            autofocus={this.props.autofocus}
                            style={{
                                paddingRight: `${this.getPaddingRight()}px`
                            }}
                            simpleMd={true}
                            onChange={this.handleChange}
                            onFocus={this.handleFocus}
                            onBlur={this.handleBlur}
                            onKeyDown={this.handleKeyDown}
                            onSubmit={this.handleSubmit}
                            onUpPress={this.props.onUpPress}
                            onFilesPaste={this.handleFilesAdd}
                        />

                        <div className="editor-utils">
                            {
                                this.showHideFromGuests ? (
                                    <div key="hide" className="editor-util-item editor-util-item-hidden with-divider" onClick={this.habdleToggleHideFromGuests}>
                                        <Tooltip tooltip={this.state.hideFromGuests ? t('Show message to everyone') : t('Don\'t show message to Guests')}>
                                            <div className="editor-util-item-inner">
                                                {
                                                    this.state.hideFromGuests ? (
                                                        <IconEyeStrike />
                                                    ) : (
                                                        <IconEye />
                                                    )
                                                }
                                            </div>
                                        </Tooltip>
                                    </div>
                                ) : null
                            }
                            {
                                this.props.utils.includes('gif') ? (
                                    <GifPicker onSelect={this.handleGifSelect}>
                                        <div key="gif" className="editor-util-item">
                                            <IconGif size="18px" />
                                        </div>
                                    </GifPicker>
                                ) : null
                            }

                            {
                                this.props.utils.includes('emoji') ? (
                                    <EmojiPicker onSelect={this.handleEmojiSelect}>
                                        <div key="emoji" className="editor-util-item">
                                            <IconEmoji size="18px" />
                                        </div>
                                    </EmojiPicker>
                                ) : null
                            }

                            <div className="editor-send" onClick={this.handleSendClick}>
                                <IconSend />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default withTranslation(['chat', 'file', 'doc', 'common', 'apps'])(DiscussionInput);
